import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { BASE } from '../utils/api'

const T = {
  bg: '#ffffff',
  bg2: '#fafafa',
  bg3: '#f4f4f5',
  text: '#09090b',
  text2: '#52525b',
  text3: '#a1a1aa',
  border: '#e4e4e7',
  accent: '#6366f1',
  accentHover: '#4f46e5',
  accentLight: 'rgba(99,102,241,0.08)',
  accentBorder: 'rgba(99,102,241,0.25)',
  font: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
}

const TAGS = ['Лендинг', 'Портфолио', 'Магазин', 'Блог', 'Ресторан', 'Агентство', 'Стартап', 'Личный']

interface ShowcaseItem {
  id: string
  name: string
  slug: string
  favicon: string | null
  publicListingTags: string[]
  publishedAt: string | null
}

interface GalleryResponse {
  items: ShowcaseItem[]
  total: number
  page: number
  pages: number
}

function SiteCard({ site }: { site: ShowcaseItem }) {
  const [hovered, setHovered] = useState(false)
  const initial = site.name.charAt(0).toUpperCase()
  const tagColor = ['#6366f1', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#14b8a6']
  const colorIndex = site.name.charCodeAt(0) % tagColor.length
  const bgColor = tagColor[colorIndex]

  return (
    <a
      href={`https://${site.slug}.mybst.ru`}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'block',
        borderRadius: 14,
        border: `1px solid ${hovered ? T.accentBorder : T.border}`,
        overflow: 'hidden',
        transition: 'all 0.2s',
        transform: hovered ? 'translateY(-4px)' : 'none',
        boxShadow: hovered ? '0 12px 40px rgba(99,102,241,0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
        textDecoration: 'none',
        color: 'inherit',
        background: T.bg,
      }}
    >
      {/* Preview area */}
      <div style={{
        height: 180,
        background: `linear-gradient(135deg, ${bgColor}22 0%, ${bgColor}08 100%)`,
        borderBottom: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          width: 64,
          height: 64,
          borderRadius: 18,
          background: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 28,
          fontWeight: 900,
          color: '#fff',
          boxShadow: `0 8px 24px ${bgColor}55`,
        }}>
          {site.favicon ? (
            <img src={site.favicon} alt="" style={{ width: 36, height: 36, borderRadius: 8 }} />
          ) : initial}
        </div>
        <div style={{
          position: 'absolute',
          top: 10,
          right: 10,
          background: 'rgba(255,255,255,0.9)',
          backdropFilter: 'blur(8px)',
          borderRadius: 6,
          padding: '3px 8px',
          fontSize: 11,
          fontWeight: 600,
          color: T.text2,
          border: `1px solid ${T.border}`,
        }}>
          ↗ Открыть
        </div>
      </div>

      {/* Info */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: T.text, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {site.name}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {site.publicListingTags.slice(0, 3).map(tag => (
            <span key={tag} style={{
              fontSize: 11,
              fontWeight: 600,
              color: T.accent,
              background: T.accentLight,
              border: `1px solid ${T.accentBorder}`,
              padding: '2px 7px',
              borderRadius: 20,
            }}>{tag}</span>
          ))}
        </div>
      </div>
    </a>
  )
}

export function ShowcasePage() {
  const navigate = useNavigate()
  const [data, setData] = useState<GalleryResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [page, setPage] = useState(1)
  const [tag, setTag] = useState('')
  const [q, setQ] = useState('')
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ page: String(page) })
      if (tag) params.set('tag', tag)
      if (q) params.set('q', q)
      const res = await fetch(`${BASE}/public/sites?${params}`)
      if (!res.ok) throw new Error('Ошибка загрузки')
      setData(await res.json() as GalleryResponse)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setLoading(false)
    }
  }, [page, tag, q])

  useEffect(() => { void load() }, [load])

  // Fix body overflow from editor CSS
  useEffect(() => {
    document.body.style.overflow = 'auto'
    document.documentElement.style.overflow = 'auto'
    document.body.style.height = 'auto'
    document.documentElement.classList.remove('dark')
    return () => {
      document.body.style.overflow = ''
      document.documentElement.style.overflow = ''
      document.body.style.height = ''
    }
  }, [])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setQ(search)
    setPage(1)
  }

  const handleTag = (t: string) => {
    setTag(prev => prev === t ? '' : t)
    setPage(1)
  }

  return (
    <div style={{ background: T.bg, color: T.text, fontFamily: T.font, minHeight: '100vh' }}>
      <style>{`* { box-sizing: border-box; margin: 0; padding: 0; } a { text-decoration: none; color: inherit; }`}</style>

      {/* Header */}
      <header style={{ position: 'sticky', top: 0, zIndex: 100, background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(12px)', borderBottom: `1px solid ${T.border}` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', height: 60, display: 'flex', alignItems: 'center', gap: 16, padding: '0 24px' }}>
          <button onClick={() => navigate('/')} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontFamily: T.font }}>
            <div style={{ width: 28, height: 28, borderRadius: 8, background: T.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: '#fff', fontSize: 13 }}>B</div>
            <span style={{ fontWeight: 900, fontSize: 18, letterSpacing: -0.5, color: T.text }}>BST</span>
          </button>
          <span style={{ color: T.border, fontSize: 18 }}>/</span>
          <span style={{ fontSize: 15, fontWeight: 600, color: T.text2 }}>Витрина</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => navigate('/login')} style={{ fontSize: 14, fontWeight: 600, color: T.text2, background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.accentBorder; e.currentTarget.style.color = T.accent }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.text2 }}
          >
            Войти
          </button>
          <button onClick={() => navigate('/login')} style={{ fontSize: 14, fontWeight: 700, color: '#fff', background: T.accent, border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontFamily: T.font, transition: 'background 0.15s' }}
            onMouseEnter={e => (e.currentTarget.style.background = T.accentHover)}
            onMouseLeave={e => (e.currentTarget.style.background = T.accent)}
          >
            Создать сайт →
          </button>
        </div>
      </header>

      {/* Hero */}
      <section style={{ padding: '64px 24px 48px', textAlign: 'center', background: T.bg2, borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: T.accent, background: T.accentLight, border: `1px solid ${T.accentBorder}`, padding: '4px 12px', borderRadius: 20, marginBottom: 16 }}>
          Витрина BST
        </div>
        <h1 style={{ fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: 900, letterSpacing: -1.5, color: T.text, marginBottom: 14, lineHeight: 1.1 }}>
          Сайты, созданные на BST
        </h1>
        <p style={{ fontSize: 17, color: T.text2, maxWidth: 480, margin: '0 auto 32px', lineHeight: 1.65 }}>
          Реальные проекты наших пользователей. Вдохновляйтесь и создавайте своё.
        </p>

        {/* Search */}
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, maxWidth: 480, margin: '0 auto' }}>
          <input
            type="text"
            placeholder="Поиск по названию..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, padding: '11px 16px', borderRadius: 9, border: `1px solid ${T.border}`, fontSize: 14, fontFamily: T.font, color: T.text, background: T.bg, outline: 'none' }}
            onFocus={e => (e.currentTarget.style.borderColor = T.accent)}
            onBlur={e => (e.currentTarget.style.borderColor = T.border)}
          />
          <button type="submit" style={{ padding: '11px 20px', background: T.accent, color: '#fff', border: 'none', borderRadius: 9, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, transition: 'background 0.15s', whiteSpace: 'nowrap' }}
            onMouseEnter={e => (e.currentTarget.style.background = T.accentHover)}
            onMouseLeave={e => (e.currentTarget.style.background = T.accent)}
          >
            Найти
          </button>
        </form>
      </section>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '32px 24px' }}>

        {/* Tag filters */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 32 }}>
          <button
            onClick={() => handleTag('')}
            style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${!tag ? T.accent : T.border}`, background: !tag ? T.accentLight : T.bg, color: !tag ? T.accent : T.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s' }}
          >
            Все
          </button>
          {TAGS.map(t => (
            <button
              key={t}
              onClick={() => handleTag(t)}
              style={{ padding: '6px 14px', borderRadius: 20, border: `1px solid ${tag === t ? T.accent : T.border}`, background: tag === t ? T.accentLight : T.bg, color: tag === t ? T.accent : T.text2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, transition: 'all 0.15s' }}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} style={{ borderRadius: 14, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
                <div style={{ height: 180, background: T.bg3, animation: 'skeleton 1.4s ease infinite' }} />
                <div style={{ padding: '14px 16px' }}>
                  <div style={{ height: 16, width: '70%', background: T.bg3, borderRadius: 6, marginBottom: 10, animation: 'skeleton 1.4s ease infinite' }} />
                  <div style={{ height: 20, width: '40%', background: T.bg3, borderRadius: 10, animation: 'skeleton 1.4s ease infinite' }} />
                </div>
              </div>
            ))}
            <style>{`@keyframes skeleton { 0%,100%{opacity:1}50%{opacity:0.5} }`}</style>
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '80px 24px', color: T.text3 }}>
            <div style={{ fontSize: 40, marginBottom: 16 }}>⚠️</div>
            <p style={{ fontSize: 16, color: T.text2 }}>{error}</p>
            <button onClick={() => void load()} style={{ marginTop: 16, padding: '9px 20px', background: T.accent, color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: T.font, fontSize: 14, fontWeight: 600 }}>
              Повторить
            </button>
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 24px' }}>
                <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
                <p style={{ fontSize: 17, color: T.text2, marginBottom: 8 }}>Ничего не найдено</p>
                <p style={{ fontSize: 14, color: T.text3 }}>Попробуйте другой запрос или тег</p>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                  <span style={{ fontSize: 14, color: T.text3 }}>
                    {q ? `Результаты для «${q}»: ` : ''}{data.total} {data.total === 1 ? 'сайт' : data.total < 5 ? 'сайта' : 'сайтов'}
                  </span>
                  {q && (
                    <button onClick={() => { setQ(''); setSearch(''); setPage(1) }} style={{ fontSize: 13, color: T.accent, background: 'none', border: 'none', cursor: 'pointer', fontFamily: T.font }}>
                      Сбросить поиск ✕
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
                  {data.items.map(site => <SiteCard key={site.id} site={site} />)}
                </div>

                {/* Pagination */}
                {data.pages > 1 && (
                  <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 48 }}>
                    <button
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: page === 1 ? T.text3 : T.text, cursor: page === 1 ? 'default' : 'pointer', fontFamily: T.font, fontSize: 14, fontWeight: 500, opacity: page === 1 ? 0.5 : 1 }}
                    >
                      ← Назад
                    </button>
                    {Array.from({ length: data.pages }, (_, i) => i + 1).filter(p => Math.abs(p - page) <= 2 || p === 1 || p === data.pages).map((p, idx, arr) => (
                      <span key={p}>
                        {idx > 0 && arr[idx - 1] !== p - 1 && <span style={{ padding: '8px 4px', color: T.text3, fontSize: 14 }}>…</span>}
                        <button
                          onClick={() => setPage(p)}
                          style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${p === page ? T.accent : T.border}`, background: p === page ? T.accentLight : T.bg, color: p === page ? T.accent : T.text, cursor: 'pointer', fontFamily: T.font, fontSize: 14, fontWeight: p === page ? 700 : 500 }}
                        >
                          {p}
                        </button>
                      </span>
                    ))}
                    <button
                      onClick={() => setPage(p => Math.min(data.pages, p + 1))}
                      disabled={page === data.pages}
                      style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.bg, color: page === data.pages ? T.text3 : T.text, cursor: page === data.pages ? 'default' : 'pointer', fontFamily: T.font, fontSize: 14, fontWeight: 500, opacity: page === data.pages ? 0.5 : 1 }}
                    >
                      Вперёд →
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>

      {/* CTA footer */}
      <section style={{ borderTop: `1px solid ${T.border}`, padding: '56px 24px', textAlign: 'center', background: T.bg2, marginTop: 40 }}>
        <h2 style={{ fontSize: 28, fontWeight: 900, letterSpacing: -0.5, color: T.text, marginBottom: 12 }}>Создайте свой сайт</h2>
        <p style={{ fontSize: 16, color: T.text2, marginBottom: 28 }}>Бесплатно, без кода, за 5 минут.</p>
        <button onClick={() => navigate('/login')} style={{ padding: '13px 32px', background: T.accent, color: '#fff', border: 'none', borderRadius: 9, fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: T.font, transition: 'background 0.15s' }}
          onMouseEnter={e => (e.currentTarget.style.background = T.accentHover)}
          onMouseLeave={e => (e.currentTarget.style.background = T.accent)}
        >
          Начать бесплатно →
        </button>
      </section>
    </div>
  )
}
